import { useState } from "react";
import { Calendar } from "lucide-react";
import { format, addDays, addHours } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { generateShare } from "@/services/share-service";
import type { UploadedFile } from "./FileUploadZone";

interface ShareLinkModalProps {
  isOpen: boolean;
  files: UploadedFile[];
  onClose: () => void;
}

const ShareLinkModal = ({ isOpen, files, onClose }: ShareLinkModalProps) => {
  const [expiryEnabled, setExpiryEnabled] = useState(false);
  const [expiryType, setExpiryType] = useState<"duration" | "date">("duration");
  const [durationType, setDurationType] = useState<"hours" | "days">("days");
  const [durationValue, setDurationValue] = useState("7");
  const [expiryDate, setExpiryDate] = useState<Date>();
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [password, setPassword] = useState("");

  const handleGenerateLink = async () => {
    try {
      let expiresAt: Date | null = null;

      if (expiryEnabled) {
        if (expiryType === "duration") {
          const duration = parseInt(durationValue);
          expiresAt =
            durationType === "hours"
              ? addHours(new Date(), duration)
              : addDays(new Date(), duration);
        } else {
          expiresAt = expiryDate;
        }
      }

      const shareData = {
        files: files.map(({ progress, ...rest }) => rest),
        expiresAt,
        password: passwordEnabled ? password : null,
      };

      const { id } = await generateShare(shareData);
      const shareLink = `${window.location.origin}/share/${id}`;

      await navigator.clipboard.writeText(shareLink);
      toast.success("Link copied to clipboard!");
      onClose();
    } catch (error) {
      toast.error("Failed to generate share link", {
        description: error.message || "Please try again",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Configure Share Link</DialogTitle>
          <DialogDescription>
            Set up expiration and password protection for your shared files.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="expiry-toggle">Enable Expiry</Label>
            <Switch
              id="expiry-toggle"
              checked={expiryEnabled}
              onCheckedChange={setExpiryEnabled}
            />
          </div>

          {expiryEnabled && (
            <>
              <RadioGroup
                value={expiryType}
                onValueChange={(value) =>
                  setExpiryType(value as "duration" | "date")
                }
                className="flex space-x-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="duration" id="duration" />
                  <Label htmlFor="duration">Duration</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="date" id="date" />
                  <Label htmlFor="date">Specific Date</Label>
                </div>
              </RadioGroup>

              {expiryType === "duration" ? (
                <div className="flex space-x-2">
                  <Input
                    type="number"
                    value={durationValue}
                    onChange={(e) => setDurationValue(e.target.value)}
                    className="w-20"
                    min="1"
                  />
                  <Select
                    value={durationType}
                    onValueChange={(value) =>
                      setDurationType(value as "hours" | "days")
                    }
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="flex flex-col space-y-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="justify-start text-left font-normal"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {expiryDate ? (
                          format(expiryDate, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={expiryDate}
                        onSelect={setExpiryDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </>
          )}

          <div className="flex items-center justify-between">
            <Label htmlFor="password-toggle">Password Protection</Label>
            <Switch
              id="password-toggle"
              checked={passwordEnabled}
              onCheckedChange={setPasswordEnabled}
            />
          </div>

          {passwordEnabled && (
            <div className="flex flex-col space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            onClick={handleGenerateLink}
            disabled={passwordEnabled && !password}
          >
            Generate & Copy Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareLinkModal;
